import { Stack, Spacer } from '@chakra-ui/react';
import { noop } from 'lodash-es';
import { useLocation, Link as ReactRouterLink } from 'react-router-dom';
import { useStatsigClient } from '@statsig/react-bindings';
import { useState } from 'react';

import {
  usePrefetchAgendaMeetings,
  usePrefetchFeedCards,
  usePrefetchFeedInsights
} from '@util/hooks';
import { useGlobalSearchStore, useUserStore, useRefStore } from '@store/app.store';
import { PAGE_HEADER_HEIGHT } from '@util/constants';
import {
  SFCalendarIcon,
  SFClockIcon,
  SFHomeIcon,
  SFLensIcon,
  SFInfoIcon,
  SFBarChartIcon,
  SFListIcon,
  SFChatBubblesIconOutlined
} from '@assets/icons';
import AddMenu from '@components/layout/add-menu.component';
import ReskinNavItem from '@components/layout/reskin-nav-item.component';

export default function Sider() {
  const [isExpanded, setIsExpanded] = useState(false);
  const { checkGate } = useStatsigClient();
  const enableInsightSharing = checkGate('insight-sharing');
  const enableCollections = checkGate('collections');
  const enableChats = checkGate('enable-chats-nav-item');

  const pathname = useLocation().pathname;
  const user = useUserStore((state) => state.user);
  const tenant = useUserStore((state) => state.tenant);
  const userType = user?.type;
  const isGuest = user?.isGuest;
  const agendaMainRef = useRefStore((state) => state.agendaMainRef);
  const setGlobalSearchInput = useGlobalSearchStore((state) => state.setInput);
  const prefetchAgendaMeetings = usePrefetchAgendaMeetings();
  const prefetchFeedCards = usePrefetchFeedCards();
  const prefetchFeedInsights = usePrefetchFeedInsights();

  const feConfig = tenant?.fe_config || {};

  const handleNavItemOnClick = (to, isActive) => {
    if (to === 'summaries') {
      agendaMainRef.current.scrollTop = 0;
    }

    return !isActive && setGlobalSearchInput('');
  };

  const pagesMap = {
    agenda: {
      title: 'agenda',
      to: '/agenda',
      icon: SFCalendarIcon,
      disabled: userType === 'Associate' || !(feConfig?.agenda_enabled ?? true),
      onHover: () => prefetchAgendaMeetings(new Date())
    },
    home: {
      title: 'home',
      to: isGuest ? '/' : '/home',
      icon: SFHomeIcon,
      disabled: userType === 'Associate' || !(feConfig?.home_enabled ?? true),
      iconProps: { boxSize: '28px' },
      onHover: () => prefetchFeedInsights()
    },
    chats: {
      title: 'chats',
      to: '/conversations-history?tags=type-card-explorer',
      icon: SFChatBubblesIconOutlined,
      disabled: userType === 'Associate' || !(enableChats ?? true),
      iconProps: { boxSize: '28px' },
      onHover: noop
    },
    lenses: {
      title: 'lenses',
      to: '/lenses',
      disabled: userType === 'Associate' || !(feConfig?.lenses_enabled ?? true),
      icon: SFLensIcon,
      iconProps: { boxSize: 8 },
      onHover: noop
    },
    insights: {
      title: 'insights',
      to: '/insights',
      icon: SFBarChartIcon,
      disabled:
        !enableInsightSharing || userType === 'Associate' || !(feConfig?.insights_enabled ?? true),
      iconProps: { boxSize: '28px' },
      onHover: () => prefetchFeedInsights()
    },
    info: {
      title: 'info',
      to: 'https://fora.day/product',
      icon: SFInfoIcon,
      iconProps: { boxSize: '28px', color: 'surface.onSurfaceVariant' },
      onHover: noop,
      target: '_blank'
    },
    recent: {
      title: 'recent',
      to: '/summaries',
      icon: SFClockIcon,
      disabled: userType === 'Associate' || !(feConfig?.summaries_enabled ?? true),
      onHover: () => prefetchFeedCards()
    },
    collections: {
      title: 'collections',
      to: '/collections',
      icon: SFListIcon,
      iconProps: { boxSize: 8 },
      disabled:
        !enableCollections || userType === 'Associate' || !(feConfig?.collections_enabled ?? true),
      onHover: noop
    }
  };

  const { agenda, home, insights, chats, recent, info, collections } = pagesMap;

  const guestPages = [home, ...(enableInsightSharing ? [insights] : []), info];

  const userPages = [agenda, insights, recent, chats, collections].filter((p) => !p.disabled);

  const pages = isGuest ? guestPages : userPages;

  return (
    <Stack
      as='nav'
      h={`calc(100vh - ${PAGE_HEADER_HEIGHT}px)`}
      w={isExpanded ? 52 : 20}
      bgColor='surface.containerLow'
      position='fixed'
      zIndex='docked'
      pt={11}
      pb={6}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      transition='width 0.2s ease-in-out'
      boxShadow={isExpanded ? 'md' : 'none'}
    >
      {pages.map((page) => {
        const { to, title, icon, iconProps, onHover, target } = page;
        const isActive = pathname === to;
        return (
          <ReskinNavItem
            key={title}
            isActive={isActive}
            isExpanded={isExpanded}
            as={ReactRouterLink}
            to={to}
            title={title}
            icon={icon}
            iconProps={iconProps}
            onMouseEnter={onHover}
            onClick={() => handleNavItemOnClick(to, isActive)}
            target={target}
          />
        );
      })}
      {!isGuest && userType !== 'Associate' && (feConfig?.add_menu_enabled ?? true) && (
        <AddMenu isExpanded={isExpanded} />
      )}
      <Spacer />
    </Stack>
  );
}
